import { useGenerateBrandGuidelinesMutation } from "api/clarityApi";
import { useLazyGetReviewedPageIssuesQuery } from "api/collaborateApi";
import { siteApi, useAddPageMutation } from "api/siteApi";
import { ArrowLeft, CornerDownLeft, Eye, Gauge, Globe, Hammer, Info, MousePointerClick, Pencil, Search, Settings, Sparkles } from "lucide-react";
import { AnimatePresence, LayoutGroup, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import type { AiSettingsProjectType, AiTeamMateProps } from "types/api/collaborate";
import { BorderBeam, Button, buttonVariants, Input, Label, MultiSelect, ResponsiveDialog, ShineBorder, Switch, Textarea, Tooltip } from "ui";
import type { Option } from "ui/components/MultiSelect";
import { TooltipTrigger } from "ui/components/Tooltip";
import { cn } from "utils/cn";
import { normalizeUrl } from "utils/proxyUtils";
import { useShadowDomWheelScrollFix } from "../../../../ui/components/Dialog/scroll-fix";
import { useAiPageReview } from "../../hooks/use-ai-page-review";
import { useAiSettings } from "../../hooks/use-ai-review-settings";
import useGetPageId from "../../hooks/use-get-pageid";
import { usePermissions } from "../../hooks/use-permissions";
import { useAppSelector } from "../../store";
import { getSelectedSiteId } from "../../store/siteInfoSlice";
import { getLoadingAiDesignReview, setIsLoadingAi, setPageId } from "../../store/tasksSlice";
import { getUser, setShowLockFeature } from "../../store/userSlice";

// Using shared type for AI teammate items

interface AiPageReviewModalProps {
    buttonText?: string;
}

const PROJECT_TYPE_OPTIONS: Option[] = [
    { label: 'Marketing Website', value: 'marketing-website' },
    { label: 'eCommerce', value: 'ecommerce' },
    { label: 'SaaS Platform', value: 'saas-platform' },
    { label: 'Portfolio', value: 'portfolio' },
    { label: 'Corporate Website', value: 'corporate-website' },
    { label: 'Landing Page', value: 'landing-page' },
    { label: 'Web App', value: 'web-app' },
    { label: 'Internal Tool', value: 'internal-tool' },
    { label: 'Membership Site', value: 'membership-site' },
    { label: 'Blog or News Site', value: 'blog-news-site' },
    { label: 'Non-Profit Website', value: 'non-profit-website' },
    { label: 'Government Website', value: 'government-website' },
    { label: 'Educational Platform', value: 'educational-platform' },
    { label: 'Event or Conference Site', value: 'event-conference-site' },
    { label: 'Community Forum', value: 'community-forum' },
    { label: 'Client Portal', value: 'client-portal' },
    { label: 'Product Documentation Site', value: 'product-documentation-site' },
    { label: 'Multilingual Website', value: 'multilingual-website' },
    { label: 'Custom Web Solution', value: 'custom-web-solution' },
];

const isOptionLike = (item: unknown): item is AiSettingsProjectType => {
    if (!item || typeof item !== 'object') return false;
    const obj = item as { label?: unknown; value?: unknown };
    return typeof obj.label === 'string' && typeof obj.value === 'string';
};

// Removed segmented toggle in favor of a switch that mirrors SettingsModal behavior



export const AiPageReviewModal = ({ buttonText = 'Review' }: AiPageReviewModalProps) => {
    const dispatch = useDispatch();
    const { isPermitted } = usePermissions();
    const { pageId } = useGetPageId();
    const [isAdvanced, setIsAdvanced] = useState(false);
    const siteId = useSelector(getSelectedSiteId);
    const userInfo = useSelector(getUser);
    const [fetch, { isFetching }] = useLazyGetReviewedPageIssuesQuery();
    const loadingAiDesignReview = useAppSelector(getLoadingAiDesignReview);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    useShadowDomWheelScrollFix()
    const [addPage, { isLoading }] = useAddPageMutation();
    const { t } = useTranslation();
    const [globalSettings, setGlobalSettings] = useState(false);
    const { currentPage } = useGetPageId();
    const [brandGuidelineUrl, setBrandGuidelineUrl] = useState(currentPage?.target_url || '');
    const [generateBrandGuidelines, { isLoading: isGeneratingBrandGuidelines }] = useGenerateBrandGuidelinesMutation();

    const suggestedIntents: { label: string; value: string; icon: JSX.Element; agents: string[] }[] = [
        {
            label: 'Perfect my design',
            icon: <Pencil size={14} />,
            value:
                `Review this page for design precision and UI consistency. Focus on alignment, spacing, grid adherence, typography hierarchy, brand color use, and component consistency. Identify any visual drift from the design system or brand style guide. Give me specific fixes tied to visible UI elements. Align the page with my business goals, brand and audience.`,
            agents: ['pixel'],
        },
        {
            label: 'Find accessibility gaps',
            icon: <Eye size={14} />,
            value:
                'Audit this page for accessibility compliance (WCAG 2.1 AA). Check contrast ratios, keyboard navigation, focus order, ARIA labels, alternative text, touch target size, form field labeling, and semantic HTML structure. Flag barriers for users with visual, motor, cognitive, or hearing impairments. Give me clear, actionable fixes tied to specific elements.',
            agents: ['navi'],
        },

        {
            label: 'Increase conversion rate',
            icon: <MousePointerClick size={14} />,
            value:
                "Review this page for conversion potential. Analyze clarity and persuasiveness of CTAs, value proposition visibility, headline impact, and form usability. Check if copy supports the funnel stage, if page flow matches user intent, and if any elements create friction or distraction. Suggest specific, high-impact changes tied to elements. Point out potential measurable lifts, how the change can affect the result.",
            agents: ['navi', 'lexi'],
        },
        {
            label: 'Make this page faster',
            icon: <Gauge size={14} />,
            value:
                "Analyze this page for performance bottlenecks. Focus on Core Web Vitals (LCP, CLS, FID/TBT), render-blocking resources, image optimization, lazy loading, code splitting, caching, and third-party script impact. Recommend specific, actionable fixes tied to assets, elements, or network calls. Include any measurable improvement estimates if possible.",
            agents: ['index'],
        },
        {
            label: 'Improve SEO',
            icon: <Search size={14} />,
            value:
                'Review this page for SEO improvements. Check metadata, structured data, headings hierarchy, internal linking, image alt text, mobile optimization, URL structure, and keyword targeting. Evaluate on-page copy for keyword relevance, clarity, and alignment with search intent. Identify specific, high-value changes tied to visible elements. Align the page with my business goals, voice and audience.',
            agents: ['index', 'lexi'],
        },
        {
            label: 'Fix what\'s broken',
            icon: <Hammer size={14} />,
            value:
                'Review this page for functional issues. Test all visible interactive elements — buttons, links, forms, navigation, modals, carousels, accordions, video players, and dynamic components. Identify any broken links, unresponsive elements, console errors, form validation failures, or features that behave unexpectedly across devices. Report specific, reproducible bugs tied to visible UI elements.',
            agents: ['glitch'],
        },

        {
            label: 'Custom',
            icon: <Pencil size={14} />,
            value: '',
            agents: [],
        }

    ];

    const BOT_QUIPS: Record<string, string> = {
        pixel: 'ready to lock in crisp, consistent UI',
        navi: 'ready to navigate UX and accessibility',
        index: 'ready to boost SEO and performance',
        lexi: 'ready to polish copy and tone',
        glitch: 'ready to squash broken links',
    };

    const [selectedIntent, setSelectedIntent] = useState<string | null>(null);

    const {
        formData: aiFormData,
        botsWithState,
        isProcessing,
        updateFormField,
        handleBotToggle,
        setReviewIntent,
        reviewIntent,
    } = useAiSettings(globalSettings);


    const { loading, handleScrollingReview, } = useAiPageReview();
    const isGeneratingAiDesignReview = loadingAiDesignReview[pageId + 'running']
    const showDesignReviewIndicator = isGeneratingAiDesignReview === 'running' || isGeneratingAiDesignReview === 'pending';


    // update the AI loading state to be true when the review is in progress
    useEffect(() => {
        dispatch(setIsLoadingAi(loading || isFetching));
    }, [dispatch, loading, isFetching]);

    useEffect(() => {
        textAreaRef.current?.focus();
    }, []);

    // Ensure all bots are OFF on initial page load
    useEffect(() => {
        updateFormField('enabledBots', []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep current selections across open/close (no resets on close)

    useEffect(() => {
        if (currentPage) {
            setBrandGuidelineUrl(currentPage.target_url || '');
        }
    }, [currentPage, globalSettings]);


    const handleGenerateBrandGuidelines = async () => {
        const trimmedUrl = brandGuidelineUrl.trim();
        if (!trimmedUrl) {
            toast.error('Please enter a URL');
            return;
        }
        // Use URL constructor for robust validation, allow protocol-less domains by prepending https://
        let urlToValidate = trimmedUrl;
        if (!/^https?:\/\//i.test(urlToValidate)) {
            urlToValidate = "https://" + urlToValidate;
        }
        let isValidUrl = false;
        try {
            // Throws if invalid
            new URL(urlToValidate);
            isValidUrl = true;
        } catch {
            isValidUrl = false;
        }
        if (!isValidUrl) {
            toast.error('Please enter a valid website URL (e.g. https://example.com or example.com)');
            return;
        }
        try {
            const response = await generateBrandGuidelines(trimmedUrl);
            if (response.data?.success) {
                updateFormField('brandName', response.data.summary.brandName);
                // Map API string[] to AiSettingsProjectType[] (label/value)
                const raw: unknown = response.data.summary.projectTypes;
                const projectTypes: AiSettingsProjectType[] = Array.isArray(raw)
                    ? raw
                        .map((item: unknown) => {
                            if (typeof item === 'string') {
                                const match = PROJECT_TYPE_OPTIONS.find((opt) => opt.value === item);
                                if (match) return { label: match.label, value: match.value };
                                const label = item
                                    .replace(/-/g, ' ')
                                    .replace(/\b\w/g, (c) => c.toUpperCase());
                                return { label, value: item };
                            }
                            if (isOptionLike(item)) return { label: item.label, value: item.value };
                            return null;
                        })
                        .filter((i: AiSettingsProjectType | null): i is AiSettingsProjectType => !!i)
                        .slice(0, 3)
                    : [];
                updateFormField('projectTypes', projectTypes);
                updateFormField('targetAudience', response.data.summary.targetAudience);
                updateFormField('voiceTone', response.data.summary.toneOfVoice);
                updateFormField('customGuidelines', response.data.summary.additionalContext);
                setBrandGuidelineUrl('');
                toast.success('Brand guidelines generated successfully!');
            } else {
                toast.error('Failed to generate brand guidelines');
            }
        } catch (_e) {
            toast.error('Failed to generate brand guidelines');
        }
    };

    const onReviewClick = async () => {
        try {
            if (!pageId) {

                try {
                    const response = await addPage({
                        page_url: normalizeUrl(window.location.href),
                        site_id: siteId || '',
                    }).unwrap();

                    if (!response.status || !response.result?.id) {
                        throw new Error('Invalid response from page creation');
                    }

                    const currentPageId = response.result.id;
                    // Update the store with the new pageId immediately to trigger socket connection
                    dispatch(setPageId(currentPageId));

                    // Force refresh of pages query to ensure useGetPageId hook gets the new page
                    dispatch(siteApi.util.invalidateTags([{ type: 'Pages', id: 'LIST' }]));

                    const reviewedIssues = await fetch({
                        pageId: currentPageId,
                        status: 'pending',
                    }, true).unwrap();
                    await handleScrollingReview(reviewedIssues, {}, aiFormData, currentPageId);

                } catch (err: unknown) {
                    const message = (err as { data?: { message?: string } })?.data?.message;
                    if (typeof message === 'string' && message.includes('Unauthenticated')) {
                        toast.error(t('page_footer.add_error_unauthenticated', 'You are not authorized to add pages to this project'));
                    } else {
                        toast.error(t('page_footer.add_error', 'Failed to add page to project'));
                    }
                    return;
                }
                return;
            } else {
                try {

                    const reviewedIssues = await fetch({
                        pageId,
                        status: 'pending',
                    }, true).unwrap();

                    await handleScrollingReview(reviewedIssues, {}, aiFormData);
                } catch (reviewError) {
                    console.error('❌ Failed to fetch existing issues:', reviewError);
                }
            }

        } catch (error) {
            console.error("Review failed:", error);
        }
    };

    const isReviewDisabled = botsWithState.every((bot: AiTeamMateProps) => !bot.enabled)
    const isLoadingReview = isProcessing || showDesignReviewIndicator;

    // update the AI loading state to be true when the review is in progress
    useEffect(() => {
        dispatch(setIsLoadingAi(loading || isFetching));
    }, [dispatch, loading, isFetching]);

    useEffect(() => {
        textAreaRef.current?.focus();
    }, []);

    // Check if user has permission to review pages
    if (!isPermitted("collaborate-sidebar-review-page")) {
        return null;
    }

    return (
        <ResponsiveDialog>
            <ResponsiveDialog.Trigger
                disabled={showDesignReviewIndicator || loading || isLoading}
                className={cn(showDesignReviewIndicator && "opacity-50")}
            >
                <Tooltip content={showDesignReviewIndicator ? "Hold tight—there’s already a review in progress. Start a new one once it wraps up." : "Review the current view"} portal>
                    <span className={cn(buttonVariants({
                        variant: 'outline',
                        size: 'sm',
                    }))}
                    >
                        {showDesignReviewIndicator && <ShineBorder borderWidth={1} duration={12} shineColor={["#A07CFE", "#FE8FB5", "#FFBE7B"]} />}
                        {showDesignReviewIndicator && <BorderBeam
                            duration={4}
                            size={50}
                            borderWidth={2}
                            className="from-transparent via-tertiary to-transparent"
                        />}
                        {showDesignReviewIndicator && <BorderBeam
                            duration={4}
                            delay={2}
                            size={50}
                            borderWidth={2}
                            className="from-transparent via-ata-orange to-transparent"
                        />}
                        <Sparkles size={16} />
                        {buttonText}
                    </span>
                </Tooltip>
            </ResponsiveDialog.Trigger>
            <ResponsiveDialog.Content
                className="max-w-[680px] px-6 pt-6 gap-2 max-h-[98vh] overflow-y-auto"
                container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
                data-dialog-scroll
            >
                <ResponsiveDialog.Title>
                    {isAdvanced && (
                        <>Advanced Settings</>
                    )
                    }
                </ResponsiveDialog.Title>

                <LayoutGroup id="ai-page-review-modal">
                    {/* <ResponsiveDialog.Description>
                        {isAdvanced ? (
                            <p className="text-textcolor text-sm">Tune how AI should review your project. Manage scope, brand, audience, and tone.</p>
                        ) : (
                            <p className="text-textcolor text-sm">Select the aspects you want Atarim AI to evaluate—from usability and performance to SEO—and get tailored, actionable insights.</p>
                        )}
                    </ResponsiveDialog.Description> */}
                    <motion.div layoutRoot layout="size" className="flex flex-col relative ">
                        {!isAdvanced ? (
                            <>
                                <div className="my-6">
                                    <h2 className="text-xl font-bold text-textcolor text-center mb-4">What is this review about?</h2>
                                    <div
                                        className="relative border border-border rounded-xl block"
                                        style={{
                                            boxShadow: '0 2px 6px 2px rgba(0, 0, 0, 0.05)',
                                        }}
                                    >
                                        <Textarea
                                            value={reviewIntent}
                                            data-dialog-scroll
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setReviewIntent(val);
                                                if (val.trim() === '') {
                                                    setSelectedIntent(null);
                                                }
                                            }}
                                            name="review-description"
                                            id="review-description-textarea"
                                            placeholder="Describe what you are working on..."
                                            className="h-24 w-full resize-none pr-2 rounded-t-2xl border-none outline-none focus:outline-none focus:ring-0 focus:shadow-none focus:ring-transparent focus-visible:outline-none focus-visible:ring-0 focus:ring-offset-0 focus:ring-offset-transparent">
                                        </Textarea>
                                        <div className="p-2 flex rounded-b-2xl">
                                            <Tooltip
                                                key="claro"
                                                content={
                                                    <div className="text-xs text-white">
                                                        <span className="font-semibold capitalize">Claro</span> ready to clarify and unblock
                                                    </div>
                                                }
                                                delayDuration={100}
                                                side="top"
                                                align="center"
                                                portal
                                            >
                                                <img
                                                    key="claro"
                                                    src="https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/claro.png"
                                                    alt="Claro avatar"
                                                    className={`h-6 w-6 rounded-md border border-border bg-tertiary`}
                                                />
                                            </Tooltip>
                                            <AnimatePresence>
                                                {botsWithState.filter((b) => b.enabled).map((bot) => (
                                                    <Tooltip
                                                        key={bot.id}
                                                        content={
                                                            <div className="text-xs text-white ">
                                                                <span className="font-semibold capitalize">{bot.id}</span> {BOT_QUIPS[bot.id] || 'ready to help.'}
                                                            </div>
                                                        }
                                                        delayDuration={100}
                                                        side="top"
                                                        align="center"
                                                        portal
                                                    >
                                                        <motion.img
                                                            initial={{ opacity: 0, marginLeft: 0, height: 0, width: 0 }}
                                                            animate={{ opacity: 1, height: 24, width: 24, marginLeft: 2 }}
                                                            exit={{ opacity: 0, height: 0, width: 0, marginLeft: 0 }}
                                                            src={bot.avatar}
                                                            alt={`${bot.name}-avatar`}
                                                            className={`${bot.backgroundColor} rounded-md border border-border`}
                                                        />
                                                    </Tooltip>
                                                ))}
                                            </AnimatePresence>
                                        </div>
                                    </div>
                                    <div className="mt-4 flex flex-wrap gap-2 justify-center">
                                        {suggestedIntents.map((example) => {
                                            const isSelected = selectedIntent === example.label;
                                            return (
                                                <Button
                                                    key={example.label}
                                                    size="sm"
                                                    variant={isSelected ? 'primary' : 'outline'}
                                                    aria-pressed={isSelected}
                                                    onClick={() => {
                                                        setReviewIntent(example.value);
                                                        setSelectedIntent(example.label);
                                                        updateFormField('enabledBots', example.agents);
                                                    }}
                                                    className={cn(isSelected && "bg-tertiary text-white border-tertiary dark:text-white", "shrink-0")}
                                                    leftIcon={example.icon}
                                                >
                                                    {example.label}
                                                </Button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <Label className="block my-2">Your InnerCircle</Label>
                                    <div className='grid grid-cols-2 gap-3'>
                                        {botsWithState.map((bot: AiTeamMateProps) => (
                                            <div
                                                key={bot.id}
                                                className="border-border items-center flex w-full justify-between gap-2 border p-3 rounded-xl"
                                            >
                                                <div className="flex gap-2">
                                                    <img
                                                        src={bot.avatar}
                                                        className="h-12 w-12 rounded-lg"
                                                        alt={`${bot.name}-avatar`}
                                                    />
                                                    <div>
                                                        <Label className='capitalize'>{bot.id}</Label>
                                                        <p className="text-gray-400 leading-tight text-xs">{bot.function}</p>
                                                    </div>
                                                </div>
                                                <Switch
                                                    checked={bot.enabled}
                                                    name={bot.id}
                                                    onCheckedChange={(checked) => handleBotToggle(bot.id, checked)}
                                                    disabled={isProcessing}
                                                />
                                            </div>
                                        ))}
                                        <div className="border-border items-center flex w-full justify-between gap-2 border p-3 rounded-xl">
                                            <div className="flex gap-2">
                                                <img
                                                    src="https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/claro.png"
                                                    className={`h-12 w-12 rounded-lg`}
                                                    alt="claro-avatar"
                                                />
                                                <div>
                                                    <Label className='capitalize'>Claro</Label>
                                                    <p className="text-gray-400 leading-tight text-xs">Clarity & Communication</p>
                                                </div>
                                            </div>
                                            <Tooltip content="Claro is always on. You can't turn him off." portal>

                                                <Switch
                                                    checked={true}
                                                    className="bg-tertiary"
                                                    disabled={true}
                                                    name="Claro"
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="mt-4 flex flex-col gap-6">
                                    <div className="flex flex-col">
                                        <div className="flex items-center justify-between border border-border rounded-xl p-2 pr-4">
                                            <div className='flex items-center gap-3'>
                                                <div className="flex items-center gap-3">
                                                    <div className='h-9 w-9 bg-gray-100 rounded-md flex justify-center items-center dark:bg-card text-textcolor'>
                                                        <Globe size={18} />
                                                    </div>
                                                    <div>
                                                        <Label htmlFor='global-settings' >
                                                            Use Global Settings
                                                        </Label>
                                                        <div className="text-sm text-textcolor" >
                                                            When enabled, these settings are managed from your Workspace.
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <Switch
                                                checked={globalSettings}
                                                onCheckedChange={(checked) => {
                                                    const isGlobalLocked = !(userInfo?.raw?.plan?.global_settings);
                                                    if (checked && isGlobalLocked) {
                                                        dispatch(setShowLockFeature({
                                                            showLockFeature: true,
                                                            lockTitle: 'Global AI Settings',
                                                            lockParagraph: 'Manage settings globally from your Workspace on paid plans. Upgrade to unlock.',
                                                            lockImg: 'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/integration-lock-images/lock-user-image.jpg',
                                                            rank: 2,
                                                        }));
                                                        return;
                                                    }
                                                    setGlobalSettings(checked);
                                                }}
                                                disabled={isProcessing}
                                            />
                                        </div>
                                        <AnimatePresence>
                                            {globalSettings && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10, marginTop: 0, height: 0 }}
                                                    animate={{ opacity: 1, y: 0, marginTop: 10, height: 30 }}
                                                    exit={{ opacity: 0, y: -10, marginTop: 0, height: 0 }}
                                                    transition={{
                                                        opacity: { duration: 0.18, ease: [0.4, 0, 0.2, 1] },
                                                        y: { duration: 0.22, ease: [0.4, 0, 0.2, 1] },
                                                        marginTop: { duration: 0.22, ease: [0.4, 0, 0.2, 1] },
                                                        height: { duration: 0.22, ease: [0.4, 0, 0.2, 1] }
                                                    }}
                                                    style={{ overflow: 'hidden' }}
                                                    className="flex items-center gap-1 bg-gray-100 dark:bg-card rounded-lg justify-center"
                                                >
                                                    <Info size={12} className="text-textcolor" />
                                                    <p className="text-xs text-textcolor">Global settings are enabled. Fields below are read-only.</p>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                    <div className="flex flex-col">
                                        <Label className="my-2 block">Create Brand Guidelines From Any URL</Label>
                                        <p className="text-textcolor text-sm">
                                            Instantly generate your brand guidelines—just drop in a URL, click the button, and let our AI do the rest.
                                        </p>
                                        <div className="flex gap-2 items-center mt-2">
                                            <Input
                                                placeholder="example.com"
                                                value={brandGuidelineUrl}
                                                onChange={(e) => setBrandGuidelineUrl(e.target.value)}
                                                disabled={isGeneratingBrandGuidelines || globalSettings}
                                                className="flex-1"
                                            />
                                            <Button
                                                effect={"shiny"}
                                                variant="outline"
                                                size="sm"
                                                leftIcon={<Sparkles size={16} />}
                                                loading={isGeneratingBrandGuidelines}
                                                onClick={handleGenerateBrandGuidelines}
                                                disabled={isProcessing || globalSettings}
                                            >
                                                Generate
                                            </Button>
                                        </div>
                                    </div>
                                    <div >
                                        <Label className=" mb-2 block">Brand Name</Label>
                                        <Input
                                            placeholder="e.g Acme Co"
                                            value={aiFormData.brandName}
                                            onChange={(e) => updateFormField('brandName', e.target.value)}
                                            disabled={isProcessing || globalSettings}
                                        />
                                        <p className="text-text-color mt-2 text-xs dark:text-gray-400">
                                            Used in AI suggestions to maintain brand consistency.
                                        </p>
                                    </div>
                                    <div className=''>
                                        <Label htmlFor="project-select" className=" mb-2 block">
                                            Project Type
                                        </Label>
                                        <MultiSelect
                                            options={PROJECT_TYPE_OPTIONS}
                                            value={aiFormData.projectTypes as unknown as Option[]}
                                            onChange={(values: Option[]) =>
                                                updateFormField(
                                                    'projectTypes',
                                                    values.map(v => ({ label: v.label, value: v.value })) as import('types/api/collaborate').AiSettingsProjectType[]
                                                )
                                            }
                                            placeholder="Select a project type (max 3)"
                                            hideClearAllButton
                                            maxSelected={3}
                                            disabled={isProcessing || globalSettings}
                                            loadingIndicator={
                                                <p className="text-muted-foreground py-2 text-center text-lg leading-10">
                                                    loading...
                                                </p>
                                            }
                                            emptyIndicator={
                                                <p className="text-muted-foreground w-full text-center text-sm leading-10">
                                                    No results found.
                                                </p>
                                            }
                                            rightSection={
                                                <Tooltip
                                                    content="Pressing enter on your keyboard will add the project type"
                                                    delayDuration={100}
                                                    side="bottom"
                                                    align="end"
                                                >
                                                    <TooltipTrigger asChild>
                                                        <span className="dark:bg-card text-textcolor right-1 flex h-7 w-7 min-h-7 min-w-7 items-center justify-center rounded-md bg-[#EFF2F6]">
                                                            <CornerDownLeft size={14} />
                                                        </span>
                                                    </TooltipTrigger>
                                                </Tooltip>
                                            }
                                        />
                                        <p className="text-text-color mt-2 text-xs dark:text-gray-400">
                                            Helps AI tailor feedback for your specific use case.
                                        </p>
                                    </div>

                                    <div>
                                        <Label className="mb-2 block">Target Audience</Label>
                                        <Input
                                            placeholder="e.g Designers and Frontend developers from freelancers to Fortune500."
                                            value={aiFormData.targetAudience}
                                            onChange={(e) => updateFormField('targetAudience', e.target.value)}
                                            disabled={isProcessing || globalSettings}
                                        />
                                        <p className="text-text-color mt-2 text-xs dark:text-gray-400">
                                            AI adapts recommendations based on who you're designing for.
                                        </p>
                                    </div>

                                    <div>
                                        <Label className="mb-2 block">Tone of Voice</Label>
                                        <Input
                                            placeholder="e.g Straight to the point, with a touch of positive reinforcement."
                                            value={aiFormData.voiceTone}
                                            onChange={(e) => updateFormField('voiceTone', e.target.value)}
                                            disabled={isProcessing || globalSettings}
                                        />
                                        <p className="text-text-color mt-2 text-xs dark:text-gray-400">
                                            Guides AI feedback to match your brand's communication style.
                                        </p>
                                    </div>

                                    <div style={{ scrollMarginTop: 100 }}>
                                        <Label className="mb-2 block">Add Custom AI Guidelines</Label>
                                        <p className="text-textcolor mb-3 mt-1 block text-sm">
                                            Provide specific instructions for Atarim AI to consider during
                                            reviews. Perfect for unique brand rules, design preferences, or
                                            accessibility needs.
                                        </p>
                                        <Textarea
                                            placeholder="Write your instructions..."
                                            value={aiFormData.customGuidelines}
                                            onChange={(e) => updateFormField('customGuidelines', e.target.value)}
                                            disabled={isProcessing || globalSettings}
                                            rows={4}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </motion.div>
                    <ResponsiveDialog.Footer className="px-0 mt-4">
                        <div className="w-full flex flex-row justify-between items-center">
                            {isAdvanced ? (
                                <Button
                                    className="mr-2"
                                    variant="ghost"
                                    size="sm"
                                    leftIcon={<ArrowLeft size={16} />}
                                    onClick={() => setIsAdvanced(false)}
                                >
                                    Back
                                </Button>
                            ) : (
                                <Button
                                    className="mr-2"
                                    variant="outline"
                                    size="sm"
                                    leftIcon={<Settings size={16} />}
                                    onClick={() => {
                                        const isGlobalLocked = !(userInfo?.raw?.plan?.global_settings);
                                        if (isGlobalLocked) {
                                            dispatch(setShowLockFeature({
                                                showLockFeature: true,
                                                lockTitle: 'Global AI Settings',
                                                lockParagraph: 'Manage settings globally from your Workspace on paid plans. Upgrade to unlock.',
                                                lockImg: 'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/integration-lock-images/lock-user-image.jpg',
                                                rank: 2,
                                            }));
                                            return;
                                        }
                                        setIsAdvanced(true);
                                    }}
                                >
                                    Advanced Settings
                                </Button>
                            )}
                            <ResponsiveDialog.Close asChild>
                                <Button
                                    size="sm"
                                    variant={"secondary"}
                                    onClick={onReviewClick}
                                    leftIcon={<Sparkles size={16} />}
                                    className="relative overflow-hidden"
                                    disabled={isReviewDisabled || isLoadingReview}
                                    toolTipPortal
                                    tooltip={isReviewDisabled ? "You need to turn on at least one of your AI team members above to do a review of this page." : ''}
                                >
                                    <ShineBorder borderWidth={1} duration={12} shineColor={["#A07CFE", "#FE8FB5", "#FFBE7B"]} />
                                    <BorderBeam
                                        duration={4}
                                        size={50}
                                        borderWidth={2}
                                        className="from-transparent via-tertiary to-transparent"
                                    />
                                    <BorderBeam
                                        duration={4}
                                        delay={2}
                                        size={50}
                                        borderWidth={2}
                                        className="from-transparent via-ata-orange to-transparent"
                                    />
                                    Review This Page
                                </Button>

                            </ResponsiveDialog.Close>
                        </div>
                    </ResponsiveDialog.Footer>
                </LayoutGroup>
            </ResponsiveDialog.Content>
        </ResponsiveDialog >
    );
};